import { Platform } from 'react-native';
import { useLocale } from '../contexts/LocaleContext';
import { flipIconName } from '../utils/rtl';

/**
 * Provides RTL-aware style helpers.
 * Use these instead of hardcoding marginLeft/marginRight/flexDirection.
 *
 * On web, `document.documentElement.dir = 'rtl'` is set by LocaleContext,
 * so the browser already mirrors flex row LAYOUT (rowDirection is a no-op
 * there). It does NOT flip static icon-font glyphs, so mirrorIcon still
 * swaps directional icon names on web too — only rowDirection/margin/padding
 * physical-edge helpers rely on the browser's own mirroring.
 */
export function useRTLStyles() {
  const { isRTL } = useLocale();
  // On web, CSS dir="rtl" handles flex mirroring — don't double-flip.
  const needsFlip = isRTL && Platform.OS !== 'web';

  return {
    isRTL,

    /** Physical start edge: 'left' in LTR, 'right' in RTL */
    marginStart: (value: number) =>
      isRTL ? { marginRight: value } : { marginLeft: value },

    /** Physical end edge: 'right' in LTR, 'left' in RTL */
    marginEnd: (value: number) =>
      isRTL ? { marginLeft: value } : { marginRight: value },

    paddingStart: (value: number) =>
      isRTL ? { paddingRight: value } : { paddingLeft: value },

    paddingEnd: (value: number) =>
      isRTL ? { paddingLeft: value } : { paddingRight: value },

    /** Row direction: 'row-reverse' on native RTL; 'row' on web (CSS dir handles it) */
    rowDirection: needsFlip ? ('row-reverse' as const) : ('row' as const),

    /** Text alignment at the start of the reading direction */
    textStart: isRTL ? ('right' as const) : ('left' as const),

    /** Flips directional icon names in RTL on ALL platforms — CSS dir="rtl"
     * mirrors row layout/positioning on web, but it does NOT flip the glyph
     * of a static icon font (Ionicons), so directional icons (chevrons,
     * arrows) still need their name swapped explicitly on web too, unlike
     * rowDirection/textStart which the browser already handles. */
    mirrorIcon: (name: string) => flipIconName(isRTL, name),
  };
}
